import { omit } from 'es-toolkit';
import { UnitSystem, type Unit as LibUnit, type Quantity as LibQuantity } from 'unitlib';
import { SIBaseUnits, SIFactors, IECBaseUnits } from 'unitlib/systems';

import type { DataUnit } from '#lib/server/aggregation.types.js';

const { abs, ceil, log10, min } = Math;

const baseUnits = {
  ...SIBaseUnits,
  ...IECBaseUnits,
  IO: {},
  Trans: {},
  count: {},
  event: {},
  '%': {},
  // FIXME:
  days: {},
};
const factors = omit({ ...SIFactors }, ['da', 'h', 'd', 'c']);
const derivedUnits = {};

export const unitSystem = new UnitSystem(baseUnits, factors, derivedUnits);

export type Unit = LibUnit<typeof baseUnits, typeof factors, Record<never, unknown>>;
export type Quantity = LibQuantity<typeof baseUnits, typeof factors, Record<never, unknown>>;

export const parseUnit = (unit: string | DataUnit): Unit => {
  if (typeof unit === 'string') return unitSystem.parseUnit(unit);
  else if ('simple' in unit) return unitSystem.parseUnit(unit.simple);
  else throw new Error('Invalid unit');
};
export const createUnity = () => unitSystem.createUnit({});

export function convert(val: number, from: Unit, to: Unit): number {
  return to.divide(from).divideValueByFactor(val);
}

export function formatValue(value: number, baseUnit?: Unit, displayUnit?: Unit) {
  baseUnit ??= createUnity();
  displayUnit ??= baseUnit;

  const val = convert(value, baseUnit, displayUnit);
  if (Number.isNaN(val)) return '—';
  const order = ceil(log10(abs(val)));
  const maximumSignificantDigits = order > 2 ? min(order, 9) : order > 0 ? 3 : order > -2 ? 2 : 1;
  return val.toLocaleString('cs', { maximumSignificantDigits });
}

export type CompareOp = '<' | '<=' | '=' | '!=' | '>=' | '>';

export function compare(
  a: string | number | Quantity,
  op: CompareOp,
  b: string | number | Quantity,
) {
  if (typeof a === 'string' || typeof b === 'string') {
    a = a.toString();
    b = b.toString();
  } else {
    if (typeof a === 'object' && typeof b === 'object') {
      try {
        // convert a into b's unit so the values are commensurable
        const converted = convert(a.value, a.unit, b.unit);
        if (!Number.isNaN(converted)) a = converted;
      } catch (_) {
        // incompatible units: fall back to raw values
      }
    }
    if (typeof a === 'object') a = a.value;
    if (typeof b === 'object') b = b.value;
  }

  switch (op) {
    case '<':
      return a < b;
    case '<=':
      return a <= b;
    case '=':
      return a === b;
    case '!=':
      return a !== b;
    case '>=':
      return a >= b;
    case '>':
      return a > b;
  }
}

const prettyUnits: Record<string, string | undefined> = {
  us: 'μs',
  mpercent: 'pcm',
  '1 / s': '',
};

export function formatUnit(unit: Unit) {
  const u = unit.toString();
  return prettyUnits[u] ?? u.replaceAll(' / ', '/');
}
